import { useAtomValue, useSetAtom } from "jotai";
import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FCC, UserType } from "types";

import { useQueryParam } from "@/hooks/useQueryParam";
import { DataContext } from "@/provider/DataContextProvider";
import {
  accessTokenAtom,
  destinationAtom,
  isAnonymousUserAtom,
} from "@/utils/atoms";

interface Props {
  user: UserType;
}

export const ProtectedRoute: FCC<Props> = ({ children, user }) => {
  const token = useAtomValue(accessTokenAtom);
  const setDestination = useSetAtom(destinationAtom);
  const isAnonymousUser = useAtomValue(isAnonymousUserAtom);
  const [code] = useQueryParam("code");
  const navigate = useNavigate();
  const location = useLocation();
  const { dataContext } = useContext(DataContext);
  const isEmailVerified = dataContext?.isEmailVerified ?? true;
  const authorized = !!token;

  useEffect(() => {
    const allowedPathsIfNotVerified = [
      "/welcome",
      "/welcome-esim",
      "/welcome-partner",
      "/onboarding",
    ];
    const isPartnerRoute =
      location.pathname.startsWith("/partner") ||
      location.pathname.startsWith("/welcome-partner");

    if (isAnonymousUser && isPartnerRoute)
      navigate("/welcome-partner", { replace: true });

    if (isPartnerRoute) return;

    if (
      (!authorized || !isEmailVerified) &&
      !allowedPathsIfNotVerified.some((path) =>
        location.pathname.startsWith(path),
      )
    ) {
      const searchParams = new URLSearchParams();
      if (location.pathname !== "/")
        setDestination(`${location.pathname}${location.search}`);
      if (code) searchParams.set("code", code);

      navigate({ pathname: "/welcome", search: searchParams.toString() });
    }
  }, [
    authorized,
    code,
    location.pathname,
    location.search,
    user,
    navigate,
    setDestination,
    isAnonymousUser,
    isEmailVerified,
  ]);

  return <>{children}</>;
};
